import { CREDIT_COSTS, INTEGRATED_MODEL_ID, INTEGRATED_DURATION_MIN, INTEGRATED_DURATION_MAX } from "@/config/models";

const MODEL_DURATIONS: Record<string, number> = {
  cs_480p_i2v_5s: 60,
  cs_480p_i2v_8s: 90,
  cs_720p_i2v_5s: 90,
  cs_720p_i2v_8s: 120,
  wan25_720p_i2v_5s: 240,
  // Default for 5s H3 clips (completed median ~2–3 min; pad for queue).
  integrated_h3_i2v: 180,
  cs_t2v_480p_5s: 120,
  qwen_image: 15,
  qwen_image_edit: 15,
};

/**
 * Infer H3 clip length. Prefer duration_ms (set at create, including free
 * promo gens with credits_used=0), then fall back to credits charged.
 */
export function inferClipSeconds(
  modelId: string | undefined,
  creditsUsed: number | undefined,
  durationMs?: number | null,
): number | undefined {
  if (modelId !== INTEGRATED_MODEL_ID) return undefined;
  if (durationMs && durationMs > 0) {
    const seconds = Math.round(durationMs / 1000);
    if (seconds >= INTEGRATED_DURATION_MIN && seconds <= INTEGRATED_DURATION_MAX) {
      return seconds;
    }
  }
  if (!creditsUsed) return undefined;
  const perSecond = CREDIT_COSTS[INTEGRATED_MODEL_ID];
  if (!perSecond) return undefined;
  const seconds = Math.round(creditsUsed / perSecond);
  if (seconds < INTEGRATED_DURATION_MIN || seconds > INTEGRATED_DURATION_MAX) return undefined;
  return seconds;
}

export function getEstimatedDuration(modelId: string, clipSeconds?: number): number {
  const base = MODEL_DURATIONS[modelId] ?? 60;
  if (modelId !== INTEGRATED_MODEL_ID) return base;
  // Fixed buckets — not linear with clip length.
  if (clipSeconds && clipSeconds >= 15) return 900; // ~15 min for 15s (jobs ~18 min)
  if (clipSeconds && clipSeconds >= 10) return 420; // ~7 min for 10–14s
  return base; // ~3 min for 5–9s
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) {
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }
  return `${secs}s`;
}

export function calculateProgress(
  startTime: number | null,
  modelId: string | undefined,
  clipSeconds?: number,
): number {
  if (startTime === null) return 0;
  const estimatedDuration = modelId ? getEstimatedDuration(modelId, clipSeconds) : 60;
  const elapsed = (Date.now() - startTime) / 1000;
  return Math.min(95, (elapsed / estimatedDuration) * 100);
}

export function calculateTimeRemaining(
  startTime: number | null,
  modelId: string | undefined,
  clipSeconds?: number,
): number | null {
  if (startTime === null) return null;
  const duration = modelId ? getEstimatedDuration(modelId, clipSeconds) : 60;
  const elapsed = Math.floor((Date.now() - startTime) / 1000);
  return Math.max(0, duration - elapsed);
}
